/* WizardStepper — modern segmented-pill progress stepper for the supply-side wizards.
   Each step is a rounded pill (eyebrow "Step N" + label + number/check). Done = tinted
   accent, current = solid accent with a soft glow, upcoming = muted. Brand-blue via theme. */
import React from 'react'
import PropTypes from 'prop-types'
import CheckIcon from '@mui/icons-material/Check'
import {Box, Stack, Typography} from '@mui/material'
import {alpha} from '@mui/material/styles'

export default function WizardStepper({steps, current, contextLabel}) {
  const total = steps.length
  return (
    <Box sx={{border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)', p: {xs: 1.25, md: 1.5}, mb: 2.5}}>
      <Stack direction='row' spacing={1} sx={{overflowX: 'auto'}}>
        {steps.map((s, i) => {
          const done = i < current
          const isCur = i === current
          return (
            <Box
              key={s.key || i}
              title={s.label}
              sx={{
                flex: '1 1 0',
                minWidth: 118,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 2.5,
                transition: 'all .2s',
                bgcolor: isCur ? 'primary.main' : done ? (th) => alpha(th.palette.primary.main, 0.1) : 'action.hover',
                color: isCur ? '#fff' : done ? 'primary.dark' : 'text.secondary',
                boxShadow: isCur ? (th) => `0 6px 16px ${alpha(th.palette.primary.main, 0.35)}` : 'none',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11.5,
                  fontWeight: 800,
                  bgcolor: isCur ? 'rgba(255,255,255,.25)' : done ? 'primary.main' : 'background.paper',
                  color: isCur ? '#fff' : done ? '#fff' : 'text.secondary',
                  border: !isCur && !done ? '2px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                {done ? <CheckIcon sx={{fontSize: 14}} /> : i + 1}
              </Box>
              <Box sx={{minWidth: 0}}>
                <Typography sx={{fontSize: 9, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', opacity: isCur ? 0.85 : 0.7, lineHeight: 1}}>
                  Step {i + 1}/{total}
                </Typography>
                <Typography noWrap sx={{fontSize: 12.5, fontWeight: isCur ? 700 : 600, lineHeight: 1.3, mt: 0.25}}>
                  {s.label}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Stack>
      {contextLabel ? (
        <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 1.25, textAlign: {xs: 'left', sm: 'right'}}}>{contextLabel}</Typography>
      ) : null}
    </Box>
  )
}
WizardStepper.propTypes = {
  steps: PropTypes.array.isRequired,
  current: PropTypes.number.isRequired,
  contextLabel: PropTypes.node,
}
